import { QueryPageResult, RuntimeModel, http } from '@kunlun/dependencies';
import {
  BillingBill,
  BillingBillLine,
  BillingBillOperationLog,
  DeliInvoiceInfo,
  AutoInvoiceConfig,
  PageQueryParams,
  PageResult,
  SpecifiedInvoiceContentItem,
  ImportValidateResult,
  ImportGroup,
  IndependentBillingDistributor,
  IndependentBillingSalesOrg,
  IndependentBillingCandidate,
  IndependentBillingCandidateFilter,
  IndependentBillingCreateParam
} from '../types';

const MODULE_NAME = 'deliAriesSettlement';
const SELLER_MODULE_NAME = 'deliAriesCoreSeller';
const MERCHANT_MODULE_NAME = 'deliAriesCoreMerchant';
const PLATFORM_MODULE_NAME = 'deliAriesCorePlatform';

// ==================== 开票单查询 ====================

/** 分页查询开票单列表（运营中心） */
export const queryBillingBillPage = async (params: PageQueryParams = {}) => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      billingBillQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo
            createUserName writeUserName createDate writeDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBill>>(MODULE_NAME, gql);
  return res.data.billingBillQuery.queryPage;
};

/** 分页查询开票单列表（品牌端） */
export const querySellerBillingBillPage = async (params: PageQueryParams = {}) => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      deliAriesSellerBillingBillQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo
            createUserName writeUserName createDate writeDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBill>>(SELLER_MODULE_NAME, gql);
  return res.data.deliAriesSellerBillingBillQuery.queryPage;
};

/** 分页查询开票单列表（经销工作台） */
export const queryMerchantBillingBillPage = async (params: PageQueryParams = {}) => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      deliAriesMerchantBillingBillQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo
            createUserName writeUserName createDate writeDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBill>>(MERCHANT_MODULE_NAME, gql);
  return res.data.deliAriesMerchantBillingBillQuery.queryPage;
};

/** 查询单条开票单详情（运营中心，复用 queryPage + rsql 过滤 id 取首条） */
export const queryBillingBillDetail = async (billId: number) => {
  const gql = `
    query {
      billingBillQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "id==${billId}"
          }
        ) {
          content {
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo deliveryAddress
            createUserName writeUserName createDate writeDate
          }
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBill>>(MODULE_NAME, gql);
  const content = res.data.billingBillQuery.queryPage.content;
  return content && content.length > 0 ? content[0] : null;
};

/** 查询单条开票单详情（品牌端） */
export const querySellerBillingBillDetail = async (billId: number) => {
  const gql = `
    query {
      deliAriesSellerBillingBillQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "id==${billId}"
          }
        ) {
          content {
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo deliveryAddress
            createUserName writeUserName createDate writeDate
          }
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBill>>(SELLER_MODULE_NAME, gql);
  const content = res.data.deliAriesSellerBillingBillQuery.queryPage.content;
  return content && content.length > 0 ? content[0] : null;
};

/** 查询单条开票单详情（经销工作台） */
export const queryMerchantBillingBillDetail = async (billId: number) => {
  const gql = `
    query {
      deliAriesMerchantBillingBillQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "id==${billId}"
          }
        ) {
          content {
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo deliveryAddress
            createUserName writeUserName createDate writeDate
          }
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBill>>(MERCHANT_MODULE_NAME, gql);
  const content = res.data.deliAriesMerchantBillingBillQuery.queryPage.content;
  return content && content.length > 0 ? content[0] : null;
};

// ==================== 关联单据查询 ====================

/** 分页查询开票单关联单据 */
export const queryBillingBillLines = async (
  billingBillId: number,
  params: PageQueryParams = {}
) => {
  const { currentPage = 1, size = 50, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      billingBillLineQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "billingBillId==${billingBillId} and ${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id billingBillId sourceDocType sourceDocCode tmsOrderCode tmsOrderId
            order90Code relatedDocType relatedDocCode bizTime
            salesOrgName salesOffice distributionChannel distributionChannelCode
            taxInclusiveAmount billingNetAmount billingTaxAmount
            profitCenter companyCode payer lineAmount status
            dataPackageNo mainPackageNo vatInvoiceNo
            invoiceNo deliveryOrderCode
            invoiceFileUrl invoiceFileStatus errorMsg packTime
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBillLine>>(MODULE_NAME, gql);
  return res.data.billingBillLineQuery.queryPage;
};

// ==================== 开票信息查询 ====================

/** 查询开票信息列表（运营中心） */
export const queryInvoiceInfoList = async (params: PageQueryParams = {}) => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      deliInvoiceInfoQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id code distributorId distributorCode distributorErpCode salesOrgCode
            invoiceTitle taxNo bankName bankAccount phone address deliveryAddress
            businessLicenseFile remark approvalStatus submittedBy approvedBy
            createDate writeDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<DeliInvoiceInfo>>(MODULE_NAME, gql);
  return res.data.deliInvoiceInfoQuery.queryPage;
};

/** 查询开票信息列表（品牌端，按省区过滤） */
export const querySellerInvoiceInfoList = async (params: PageQueryParams = {}) => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      deliAriesSellerInvoiceInfoQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id code distributorId distributorCode distributorErpCode salesOrgCode
            invoiceTitle taxNo bankName bankAccount phone address deliveryAddress
            businessLicenseFile remark approvalStatus submittedBy approvedBy
            createDate writeDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<DeliInvoiceInfo>>(SELLER_MODULE_NAME, gql);
  return res.data.deliAriesSellerInvoiceInfoQuery.queryPage;
};

/** 查询客户有效开票信息（不分页） */
export const queryEffectiveInvoiceInfo = async (
  distributorCode: string,
  salesOrgCode: string
) => {
  const gql = `
    query {
      deliInvoiceInfoQuery {
        queryPage(
          page: { currentPage: 1, size: 999 }
          queryWrapper: {
            rsql: "distributorCode=='${distributorCode}' and salesOrgCode=='${salesOrgCode}' and enabled==true and (approvalStatus=='APPROVED' or approvalStatus=='DIRECT_EFFECTIVE')"
          }
        ) {
          content {
            id code distributorCode distributorErpCode salesOrgCode
            invoiceTitle taxNo bankName bankAccount phone address deliveryAddress
            businessLicenseFile remark approvalStatus enabled
          }
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<DeliInvoiceInfo>>(MODULE_NAME, gql);
  return res.data.deliInvoiceInfoQuery.queryPage.content;
};

// ==================== 自动开票配置查询与导入 ====================

/** 查询自动开票配置列表 */
export const queryAutoInvoiceConfigList = async (params: PageQueryParams = {}) => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      autoInvoiceConfigQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {
            id distributorId distributorCode distributorErpCode salesOrgCode
            autoInvoiceEnabled billingType noticeInvoiceNo remark
            createDate writeDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<AutoInvoiceConfig>>(MODULE_NAME, gql);
  return res.data.autoInvoiceConfigQuery.queryPage;
};

/** 自动开票配置导入模板的模型编码与工作簿名称（与后端 AutoInvoiceConfigImportTemplate 一致） */
const AUTO_INVOICE_IMPORT_MODEL = 'deli.aries.settlement.AutoInvoiceConfig';
const AUTO_INVOICE_TEMPLATE_NAME = '自动开票配置导入';

/** 查询自动开票配置导入模板的 ExcelWorkbookDefinition id */
const queryAutoInvoiceWorkbookDefinition = async (): Promise<{ id: number; name: string } | null> => {
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "model=='${AUTO_INVOICE_IMPORT_MODEL}' and name=='${AUTO_INVOICE_TEMPLATE_NAME}' and dataStatus=='ENABLED'"
          }
        ) {
          content { id name }
        }
      }
    }
  `;
  const res = (await http.query(MODULE_NAME, gql)) as any;
  const content = res?.data?.excelWorkbookDefinitionQuery?.queryPage?.content || [];
  return content.length ? content[0] : null;
};

/** 下载自动开票配置导入模板（标准框架：fetchImportTemplateUrl） */
export const downloadAutoInvoiceTemplate = async (): Promise<string> => {
  const def = await queryAutoInvoiceWorkbookDefinition();
  if (!def) {
    throw new Error('导入模板未初始化，请联系管理员');
  }
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        fetchImportTemplateUrl(data: { id: ${def.id} }) {
          redirectUri
        }
      }
    }
  `;
  const res = (await http.query(MODULE_NAME, gql)) as any;
  return res?.data?.excelWorkbookDefinitionQuery?.fetchImportTemplateUrl?.redirectUri as string;
};

/** 发起自动开票配置标准 Excel 导入任务 */
export const createAutoInvoiceImportTask = async (file: {
  url: string;
  name?: string;
  fileName?: string;
}): Promise<{ state: string; messages: Array<{ level: string; message: string }> }> => {
  const def = await queryAutoInvoiceWorkbookDefinition();
  if (!def) {
    throw new Error('导入模板未初始化，请联系管理员');
  }
  const fileName = file.name || file.fileName || '自动开票配置导入.xlsx';
  const fileArg = JSON.stringify({ url: file.url, name: fileName }).replace(
    /"(\w+)":/g,
    '$1:'
  );
  const gql = `
    mutation {
      excelImportTaskMutation {
        createImportTask(data: {
          name: ${JSON.stringify(AUTO_INVOICE_TEMPLATE_NAME)}
          workbookDefinition: { id: ${def.id} }
          file: ${fileArg}
        }) {
          id state
          messages { level message }
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  const task = res?.data?.excelImportTaskMutation?.createImportTask || {};
  return {
    state: task.state,
    messages: task.messages || []
  };
};

// ==================== 操作接口 ====================

/** 开票单更新 */
export const updateBillingBill = async (billId: number, data: Partial<BillingBill>) => {
  const gql = `
    mutation {
      billingBillMutation {
        updateById(id: "${billId}", data: ${JSON.stringify(data).replace(/"(\w+)":/g, '$1:')}) {
          id code status
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 重置开票信息为默认（客户信息）：清空已保存的开票信息ID与手动开票信息 */
export const resetInvoiceInfo = async (billId: number) => {
  const gql = `
    mutation {
      billingBillMutation {
        resetInvoiceInfo(bill: { id: ${billId} }) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 整单确认（后端 confirm 为 @Fun ServerAction，GraphQL 参数名 = Java 方法形参名：
 *  confirm(BillingBill bill) → 参数名 bill；用 data / activeRecordId 均报 UnknownArgument。 */
export const confirmBillingBill = async (billId: number) => {
  const gql = `
    mutation {
      billingBillMutation {
        confirm(bill: { id: ${billId} }) {
          id code status
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 取消打包 */
export const cancelPackLines = async (lineIds: number[]) => {
  const idsStr = lineIds.map(id => `{id: "${id}"}`).join(',');
  const gql = `
    mutation {
      billingBillLineMutation {
        cancelPack(lines: [${idsStr}]) {
          id status
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 部分开票：保存开票信息 + 对选中行发起打包（后端统一接口） */
export const partInvoice = async (billId: number, data: {
  billingType?: string;
  remark?: string;
  invoiceInfoId?: number;
  resetInvoiceInfo?: boolean;
  deliveryAddress?: string;
  noticeInvoiceNo?: string;
  partPackLineIds: number[];
}) => {
  const lineIdsStr = data.partPackLineIds.map(id => `${id}`).join(',');
  const gql = `
    mutation {
      billingBillMutation {
        partInvoice(bill: {
          id: ${billId}
          ${data.billingType ? `billingType: ${data.billingType}` : ''}
          ${data.invoiceInfoId ? `invoiceInfoId: ${data.invoiceInfoId}` : ''}
          ${data.resetInvoiceInfo ? 'resetInvoiceInfo: true' : ''}
          ${data.deliveryAddress != null ? `deliveryAddress: ${JSON.stringify(data.deliveryAddress)}` : ''}
          ${data.remark != null ? `remark: ${JSON.stringify(data.remark)}` : ''}
          ${data.noticeInvoiceNo != null ? `noticeInvoiceNo: ${JSON.stringify(data.noticeInvoiceNo)}` : ''}
          partPackLineIds: [${lineIdsStr}]
        }) {
          id code status
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

// ==================== 开票信息操作 ====================

/** 创建开票信息 */
export const createInvoiceInfo = async (data: Partial<DeliInvoiceInfo>) => {
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        create(data: ${JSON.stringify(data).replace(/"(\w+)":/g, '$1:')}) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 更新开票信息 */
export const updateInvoiceInfo = async (infoId: number, data: Partial<DeliInvoiceInfo>) => {
  const updateData = { ...data, id: infoId };
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        update(data: ${JSON.stringify(updateData).replace(/"(\w+)":/g, '$1:')}) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 审批通过开票信息 */
export const approveInvoiceInfo = async (infoId: number) => {
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        approve(data: { id: ${infoId} }) {
          id code approvalStatus
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 审批驳回开票信息 */
export const rejectInvoiceInfo = async (infoId: number, remark?: string) => {
  const dataFields = remark ? `id: ${infoId}, rejectReason: "${remark}"` : `id: ${infoId}`;
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        reject(data: { ${dataFields} }) {
          id code approvalStatus
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

// ==================== 批量审核/删除开票信息 ====================

/** 批量审核通过开票信息 */
export const batchApproveInvoiceInfo = async (infoIds: number[]) => {
  const idsStr = infoIds.map(id => `${id}`).join(',');
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        batchApprove(data: { batchIds: [${idsStr}] }) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 单条删除开票信息 */
export const deleteInvoiceInfo = async (infoId: number) => {
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        remove(data: { id: ${infoId} }) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 批量删除开票信息 */
export const batchDeleteInvoiceInfo = async (infoIds: number[]) => {
  const idsStr = infoIds.map(id => `${id}`).join(',');
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        batchDelete(data: { batchIds: [${idsStr}] }) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 批量导出开票信息：按勾选行发起异步导出任务，文件生成后在导出任务中心下载 */
export const batchExportInvoiceInfo = async (infoIds: number[]) => {
  const idsStr = infoIds.map(id => `${id}`).join(',');
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        batchExport(data: { batchIds: [${idsStr}] }) {
          id code
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

/** 查询客户信息（经销商档案）中的开票信息，作为开票时的默认取值 */
export const queryCustomerInvoiceInfo = async (distributorCode: string) => {
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        customerInvoiceInfo(data: { distributorCode: "${distributorCode}" }) {
          distributorId distributorCode distributorErpCode
          invoiceTitle taxNo bankName bankAccount address phone
          deliveryAddress defaultBillingType
        }
      }
    }
  `;
  const res = await http.mutate<{ deliInvoiceInfoMutation: { customerInvoiceInfo: DeliInvoiceInfo | null } }>(MODULE_NAME, gql);
  return res.data?.deliInvoiceInfoMutation?.customerInvoiceInfo || null;
};

// ==================== 启用/停用开票信息 ====================

/** 启用/停用开票信息（enabled=true 调后端 enable，false 调 disable，均为 SINGLE Action） */
export const setEnabledInvoiceInfo = async (infoId: number, enabled: boolean) => {
  const action = enabled ? 'enable' : 'disable';
  const gql = `
    mutation {
      deliInvoiceInfoMutation {
        ${action}(data: { id: ${infoId} }) {
          id code enabled
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

// ==================== 指定开票 ====================

/** 指定开票（后端 SINGLE Action，specifiedInvoiceContent 仅瞬态传递、不落库）。
 *  开票信息口径与部分开票一致：保存当前页面选择 + 按勾选行打包 */
export const specifyInvoice = async (
  billId: number,
  content: SpecifiedInvoiceContentItem[],
  data: {
    billingType?: string;
    remark?: string;
    invoiceInfoId?: number;
    resetInvoiceInfo?: boolean;
    deliveryAddress?: string;
    noticeInvoiceNo?: string;
    partPackLineIds: number[];
  }
) => {
  const contentStr = JSON.stringify(JSON.stringify(content));
  const lineIdsStr = data.partPackLineIds.map(id => `${id}`).join(',');
  const gql = `
    mutation {
      billingBillMutation {
        specifyInvoice(bill: {
          id: ${billId}
          specifiedInvoiceContent: ${contentStr}
          ${data.billingType ? `billingType: ${data.billingType}` : ''}
          ${data.invoiceInfoId ? `invoiceInfoId: ${data.invoiceInfoId}` : ''}
          ${data.resetInvoiceInfo ? 'resetInvoiceInfo: true' : ''}
          ${data.deliveryAddress != null ? `deliveryAddress: ${JSON.stringify(data.deliveryAddress)}` : ''}
          ${data.remark != null ? `remark: ${JSON.stringify(data.remark)}` : ''}
          ${data.noticeInvoiceNo != null ? `noticeInvoiceNo: ${JSON.stringify(data.noticeInvoiceNo)}` : ''}
          partPackLineIds: [${lineIdsStr}]
        }) {
          id code status
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

// ==================== 刷新状态 ====================

/** 刷新开票单状态 */
export const refreshBillStatus = async (billId: number) => {
  const gql = `
    mutation {
      billingBillMutation {
        refreshStatus(bill: { id: ${billId} }) {
          id code status
        }
      }
    }
  `;
  const res = await http.mutate(MODULE_NAME, gql);
  return res.data;
};

// ==================== 导出明细 / 下载发票 / 操作日志 ====================

/** 导出模板的模型编码与工作簿名称（与后端 BillingBillLineExportTemplate 一致） */
const EXPORT_MODEL = 'deli.aries.settlement.BillingBillLine';
const EXPORT_TEMPLATE_NAME = '开票单明细导出';

/** 查询导出模板的 ExcelWorkbookDefinition id */
const queryExportWorkbookDefinition = async (): Promise<{ id: number; name: string } | null> => {
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "model=='${EXPORT_MODEL}' and name=='${EXPORT_TEMPLATE_NAME}' and dataStatus=='ENABLED' and type=in=('IMPORT_EXPORT','EXPORT')"
          }
        ) {
          content { id name }
        }
      }
    }
  `;
  const res = (await http.query(MODULE_NAME, gql)) as any;
  const content = res?.data?.excelWorkbookDefinitionQuery?.queryPage?.content || [];
  return content.length ? content[0] : null;
};

/** 导出开票单明细（调用后端 exportLines mutation，支持勾选行ID） */
export const exportBillingLines = async (billId: number, lineIds: number[]): Promise<string> => {
  const idsArg = lineIds && lineIds.length ? `[${lineIds.join(',')}]` : 'null';
  const gql = `
    mutation {
      billingBillMutation {
        exportLines(bill: { id: ${billId}, exportLineIds: ${idsArg} }) {
          id fileUrl
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.billingBillMutation?.exportLines?.fileUrl as string;
};

/** 下载发票（打包 zip，返回文件下载地址） */
export const downloadInvoiceZip = async (billId: number): Promise<string> => {
  const gql = `
    mutation {
      billingBillMutation {
        downloadInvoices(bill: { id: ${billId} }) {
          id fileUrl
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res.data.billingBillMutation.downloadInvoices.fileUrl as string;
};

/** 查询开票单操作日志（按 createDate 倒序） */
export const queryBillingOperationLogs = async (
  billingBillId: number,
  params: PageQueryParams = {}
) => {
  const { currentPage = 1, size = 100 } = params;
  const gql = `
    query {
      billingBillOperationLogQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "billingBillId==${billingBillId}"
          }
        ) {
          content {
            id billingBillId billingBillCode operationType operatorName detail createDate
          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = await http.mutate<QueryPageResult<BillingBillOperationLog>>(MODULE_NAME, gql);
  const page = res.data.billingBillOperationLogQuery.queryPage;
  // 框架查询未指定排序，前端按创建时间倒序
  page.content = (page.content || []).sort(
    (a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
  );
  return page;
};

// ==================== 导入指定开票内容（框架标准 Excel 导入） ====================

/** 导入模板的模型编码与工作簿名称（与后端 BillingBillContentImportTemplate 一致） */
const IMPORT_MODEL = 'deli.aries.settlement.BillingBillContentImport';
const IMPORT_TEMPLATE_NAME = '指定开票内容导入';

/** 查询框架导入模板（ExcelWorkbookDefinition）的 id */
export const queryImportWorkbookDefinition = async (): Promise<{ id: number; name: string } | null> => {
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "model=='${IMPORT_MODEL}' and name=='${IMPORT_TEMPLATE_NAME}'"
          }
        ) {
          content { id name }
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  const content = res?.data?.excelWorkbookDefinitionQuery?.queryPage?.content || [];
  return content.length ? content[0] : null;
};

/** 下载导入模板（框架标准：根据工作簿定义生成） */
export const downloadTemplate = async (): Promise<string> => {
  const def = await queryImportWorkbookDefinition();
  if (!def) {
    throw new Error('导入模板未初始化，请联系管理员');
  }
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        fetchImportTemplateUrl(data: { id: ${def.id} }) {
          redirectUri
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.excelWorkbookDefinitionQuery?.fetchImportTemplateUrl?.redirectUri as string;
};

/** 发起框架标准 Excel 导入任务（同步执行，返回任务结果与消息） */
export const createBillingImportTask = async (file: {
  url: string;
  name?: string;
  fileName?: string;
}): Promise<{ state: string; messages: Array<{ level: string; message: string }> }> => {
  const def = await queryImportWorkbookDefinition();
  if (!def) {
    throw new Error('导入模板未初始化，请联系管理员');
  }
  const fileName = file.name || file.fileName || '指定开票内容导入.xlsx';
  const fileArg = JSON.stringify({ url: file.url, name: fileName }).replace(
    /"(\w+)":/g,
    '$1:'
  );
  const gql = `
    mutation {
      excelImportTaskMutation {
        createImportTask(data: {
          name: ${JSON.stringify(IMPORT_TEMPLATE_NAME)}
          workbookDefinition: { id: ${def.id} }
          file: ${fileArg}
        }) {
          id state
          messages { level message }
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  const task = res?.data?.excelImportTaskMutation?.createImportTask || {};
  return {
    state: task.state,
    messages: task.messages || []
  };
};

// ==================== 开票信息批量导入（标准框架） ====================

/** 开票信息导入模板的模型编码与工作簿名称（与后端 InvoiceInfoImportTemplate 一致） */
const INVOICE_INFO_IMPORT_MODEL = 'deli.aries.settlement.InvoiceInfoImport';
const INVOICE_INFO_TEMPLATE_NAME = '开票信息导入';

/** 查询开票信息导入模板的 ExcelWorkbookDefinition id */
const queryInvoiceInfoWorkbookDefinition = async (): Promise<{ id: number; name: string } | null> => {
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "model=='${INVOICE_INFO_IMPORT_MODEL}' and name=='${INVOICE_INFO_TEMPLATE_NAME}' and dataStatus=='ENABLED'"
          }
        ) {
          content { id name }
        }
      }
    }
  `;
  const res = (await http.query(MODULE_NAME, gql)) as any;
  const content = res?.data?.excelWorkbookDefinitionQuery?.queryPage?.content || [];
  return content.length ? content[0] : null;
};

/** 下载开票信息导入模板（标准框架：fetchImportTemplateUrl） */
export const downloadInvoiceInfoTemplate = async (): Promise<string> => {
  const def = await queryInvoiceInfoWorkbookDefinition();
  if (!def) {
    throw new Error('导入模板未初始化，请联系管理员');
  }
  const gql = `
    query {
      excelWorkbookDefinitionQuery {
        fetchImportTemplateUrl(data: { id: ${def.id} }) {
          redirectUri
        }
      }
    }
  `;
  const res = (await http.query(MODULE_NAME, gql)) as any;
  return res?.data?.excelWorkbookDefinitionQuery?.fetchImportTemplateUrl?.redirectUri as string;
};

/** 发起开票信息标准 Excel 导入任务（同步执行，返回任务结果与消息） */
export const createInvoiceInfoImportTask = async (file: {
  url: string;
  name?: string;
  fileName?: string;
}): Promise<{ state: string; messages: Array<{ level: string; message: string }> }> => {
  const def = await queryInvoiceInfoWorkbookDefinition();
  if (!def) {
    throw new Error('导入模板未初始化，请联系管理员');
  }
  const fileName = file.name || file.fileName || '开票信息导入.xlsx';
  const fileArg = JSON.stringify({ url: file.url, name: fileName }).replace(
    /"(\w+)":/g,
    '$1:'
  );
  const gql = `
    mutation {
      excelImportTaskMutation {
        createImportTask(data: {
          name: ${JSON.stringify(INVOICE_INFO_TEMPLATE_NAME)}
          workbookDefinition: { id: ${def.id} }
          file: ${fileArg}
        }) {
          id state
          messages { level message }
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  const task = res?.data?.excelImportTaskMutation?.createImportTask || {};
  return {
    state: task.state,
    messages: task.messages || []
  };
};

// ==================== 独立开票 ====================

/**
 * 独立开票包列表与详情字段（运营中心 / 品牌端共用）。
 * 独立开票包与常规开票单共用 BillingBill 存储，故字段一致；额外带出 billCategory
 * 供详情页判定差异展示（隐藏对账起止时间与账单日）。
 */
const INDEPENDENT_BILL_CONTENT_FIELDS = `
            id code name distributorId distributorCode distributorErpCode distributorName
            salesOrgId salesOrgCode salesOrgName
            totalAmount invoicedAmount remainAmount status billCategory
            accountStartDate accountEndDate billDay
            billingType invoiceInfoId autoInvoice remark noticeInvoiceNo deliveryAddress
            createUserName writeUserName createDate writeDate
`;

/** 候选行字段（后端 IndependentBillingCandidate 瞬态模型） */
const INDEPENDENT_CANDIDATE_CONTENT_FIELDS = `
            id candidateId order90Code tmsOrderCode bizTime
            taxInclusiveAmount billingNetAmount billingTaxAmount
            profitCenter companyCode invoiceType distributorErpCode
            salesOrgCode salesOrgName outStockStatus
            distributionChannel distributionChannelCode sourceDocType sourceDocCode
            packSyncSuccess packSyncTime sapPackStatus
            lineStatus dataPackageNo invoiceFileUrl
`;

/** 开票信息下拉与默认开票信息字段 */
const INDEPENDENT_INVOICE_INFO_FIELDS = `
            id code distributorId distributorCode distributorErpCode salesOrgCode
            isDefault invoiceTitle taxNo bankName bankAccount phone address deliveryAddress
            businessLicenseFile remark approvalStatus enabled defaultBillingType
`;

/** 统一分页返回结构，避免后端未命中时 content 为 null 导致列表页遍历报错 */
const normalizePageResult = <T>(page: any): PageResult<T> => ({
  content: page?.content || [],
  totalPages: page?.totalPages || 0,
  totalElements: page?.totalElements || 0
});

/** 字符串多值入参：逐项 trim 后去空，拼为 GraphQL 内联数组 */
const buildStringListArg = (list?: string[]) =>
  `[${(list || [])
    .map(item => JSON.stringify(String(item ?? '').trim()))
    .filter(item => item !== '""')
    .join(',')}]`;

/**
 * 候选查询条件 → GraphQL 内联对象。
 * lineStatus 为后端枚举，GraphQL 枚举入参不加引号（与 partInvoice 的 billingType 一致）。
 */
const buildCandidateQueryArg = (query: IndependentBillingCandidateFilter) => {
  const parts: string[] = [];
  if (query.distributorCode) parts.push(`distributorCode: ${JSON.stringify(query.distributorCode)}`);
  if (query.distributorErpCode) parts.push(`distributorErpCode: ${JSON.stringify(query.distributorErpCode)}`);
  if (query.keyword) parts.push(`keyword: ${JSON.stringify(query.keyword)}`);
  if (query.salesOrgCode) parts.push(`salesOrgCode: ${JSON.stringify(query.salesOrgCode)}`);
  if (query.distributionChannelCode) parts.push(`distributionChannelCode: ${JSON.stringify(query.distributionChannelCode)}`);
  if (query.tmsOrderCodes && query.tmsOrderCodes.length) parts.push(`tmsOrderCodes: ${buildStringListArg(query.tmsOrderCodes)}`);
  if (query.order90Codes && query.order90Codes.length) parts.push(`order90Codes: ${buildStringListArg(query.order90Codes)}`);
  if (query.dataPackageNos && query.dataPackageNos.length) parts.push(`dataPackageNos: ${buildStringListArg(query.dataPackageNos)}`);
  if (query.lineStatus) parts.push(`lineStatus: ${query.lineStatus}`);
  return `{ ${parts.join(' ')} }`;
};

/** 创建入参 → GraphQL 内联对象。specifiedInvoiceContent 为指定开票 JSON 字符串，普通开票不传 */
const buildCreateParamArg = (payload: IndependentBillingCreateParam) => {
  const parts: string[] = [];
  if (payload.distributorId != null) parts.push(`distributorId: ${payload.distributorId}`);
  parts.push(`distributorCode: ${JSON.stringify(payload.distributorCode)}`);
  parts.push(`salesOrgCode: ${JSON.stringify(payload.salesOrgCode)}`);
  if (payload.invoiceInfoId != null) parts.push(`invoiceInfoId: ${payload.invoiceInfoId}`);
  if (payload.billingType) parts.push(`billingType: ${payload.billingType}`);
  if (payload.noticeInvoiceNo != null) parts.push(`noticeInvoiceNo: ${JSON.stringify(payload.noticeInvoiceNo)}`);
  if (payload.deliveryAddress != null) parts.push(`deliveryAddress: ${JSON.stringify(payload.deliveryAddress)}`);
  if (payload.remark != null) parts.push(`remark: ${JSON.stringify(payload.remark)}`);
  parts.push(`candidateIds: [${(payload.candidateIds || []).join(',')}]`);
  if (payload.specifiedInvoiceContent) {
    parts.push(`specifiedInvoiceContent: ${JSON.stringify(payload.specifiedInvoiceContent)}`);
  }
  return `{ ${parts.join(' ')} }`;
};

/** 分页查询独立开票包列表（运营中心，走 platform 代理模型） */
export const queryIndependentBillingPage = async (
  params: PageQueryParams = {}
): Promise<PageResult<BillingBill>> => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      deliAriesPlatformIndependentBillingBillQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {${INDEPENDENT_BILL_CONTENT_FIELDS}          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = (await http.mutate(PLATFORM_MODULE_NAME, gql)) as any;
  return normalizePageResult<BillingBill>(
    res?.data?.deliAriesPlatformIndependentBillingBillQuery?.queryPage
  );
};

/** 分页查询独立开票包列表（品牌端，走 seller 代理模型，后端叠加品牌数据权限） */
export const querySellerIndependentBillingPage = async (
  params: PageQueryParams = {}
): Promise<PageResult<BillingBill>> => {
  const { currentPage = 1, size = 20, rsql = '(1==1)', queryData = {} } = params;
  const gql = `
    query {
      deliAriesSellerIndependentBillingBillQuery {
        queryPage(
          page: { currentPage: ${currentPage}, size: ${size} }
          queryWrapper: {
            rsql: "${rsql}"
            queryData: ${JSON.stringify(queryData).replace(/"(\w+)":/g, '$1:')}
          }
        ) {
          content {${INDEPENDENT_BILL_CONTENT_FIELDS}          }
          totalPages
          totalElements
        }
      }
    }
  `;
  const res = (await http.mutate(SELLER_MODULE_NAME, gql)) as any;
  return normalizePageResult<BillingBill>(
    res?.data?.deliAriesSellerIndependentBillingBillQuery?.queryPage
  );
};

/**
 * 查询单条独立开票包详情（运营中心）。
 * <p>
 * 不复用 queryBillingBillDetail：基础模型与品牌端代理的 queryPage 已按 billCategory=NORMAL 收口，
 * 独立开票包必须走 INDEPENDENT 过滤的代理模型入口才能查到。
 */
export const queryIndependentBillingBillDetail = async (billId: number): Promise<BillingBill | null> => {
  const gql = `
    query {
      deliAriesPlatformIndependentBillingBillQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "id==${billId}"
          }
        ) {
          content {${INDEPENDENT_BILL_CONTENT_FIELDS}          }
          totalElements
        }
      }
    }
  `;
  const res = (await http.mutate(PLATFORM_MODULE_NAME, gql)) as any;
  const content = res?.data?.deliAriesPlatformIndependentBillingBillQuery?.queryPage?.content;
  return content && content.length > 0 ? content[0] : null;
};

/** 查询单条独立开票包详情（品牌端） */
export const querySellerIndependentBillingBillDetail = async (billId: number): Promise<BillingBill | null> => {
  const gql = `
    query {
      deliAriesSellerIndependentBillingBillQuery {
        queryPage(
          page: { currentPage: 1, size: 1 }
          queryWrapper: {
            rsql: "id==${billId}"
          }
        ) {
          content {${INDEPENDENT_BILL_CONTENT_FIELDS}          }
          totalElements
        }
      }
    }
  `;
  const res = (await http.mutate(SELLER_MODULE_NAME, gql)) as any;
  const content = res?.data?.deliAriesSellerIndependentBillingBillQuery?.queryPage?.content;
  return content && content.length > 0 ? content[0] : null;
};

/**
 * 独立开票经销商下拉：按客户 ERP 编码精准匹配，或经销商编码/名称模糊匹配。
 * 后端最多返回 50 条，keyword 为空时返回按编码升序的前 50 条。
 */
export const queryDistributorOptions = async (
  keyword?: string
): Promise<IndependentBillingDistributor[]> => {
  const gql = `
    query {
      sapBilling90OrderQuery {
        queryIndependentDistributorOptions(query: ${buildCandidateQueryArg({ keyword })}) {
          id distributorCode distributorName distributorErpCode
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.sapBilling90OrderQuery?.queryIndependentDistributorOptions || [];
};

/**
 * 独立开票销售机构下拉：仅枚举与该经销商存在有效合作关系的销售机构，
 * 默认销售机构由后端排在首位。
 */
export const querySalesOrgOptions = async (
  distributorCode: string
): Promise<IndependentBillingSalesOrg[]> => {
  const gql = `
    query {
      sapBilling90OrderQuery {
        queryIndependentSalesOrgOptions(query: ${buildCandidateQueryArg({ distributorCode })}) {
          id salesOrgId salesOrgCode salesOrgName isDefault
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.sapBilling90OrderQuery?.queryIndependentSalesOrgOptions || [];
};

/** 独立开票开票信息下拉：经销商 + 销售机构匹配、审核已通过且启用的开票信息 */
export const queryInvoiceInfoOptions = async (
  distributorCode: string,
  salesOrgCode: string
): Promise<DeliInvoiceInfo[]> => {
  const gql = `
    query {
      sapBilling90OrderQuery {
        queryIndependentInvoiceInfoOptions(query: ${buildCandidateQueryArg({ distributorCode, salesOrgCode })}) {${INDEPENDENT_INVOICE_INFO_FIELDS}        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.sapBilling90OrderQuery?.queryIndependentInvoiceInfoOptions || [];
};

/**
 * 默认开票信息：经销商客户信息（档案）中的默认开票信息，档案缺失时回退下拉中的默认项。
 * 返回 null 表示当前经销商与销售机构未维护有效开票信息，前端据此禁止提交。
 */
export const queryDefaultInvoiceInfo = async (
  distributorCode: string,
  salesOrgCode: string
): Promise<DeliInvoiceInfo | null> => {
  const gql = `
    query {
      sapBilling90OrderQuery {
        queryIndependentDefaultInvoiceInfo(query: ${buildCandidateQueryArg({ distributorCode, salesOrgCode })}) {${INDEPENDENT_INVOICE_INFO_FIELDS}        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.sapBilling90OrderQuery?.queryIndependentDefaultInvoiceInfo || null;
};

/**
 * 80/90 候选单据全量查询 + 准入校验，返回全量列表，分页由前端内存切片。
 * 不满足准入校验的行已在后端数据源上过滤，不进入返回列表。
 * 形态与 queryDistributorOptions 同构（(Filter) → List，挂 sapBilling90OrderQuery）：
 * 瞬态模型不生成 XxxPage/XxxCondInput 配套类型，标准 queryPage 覆写会启动报错；
 * Pagination 返回的自定义签名则会按 90 单模型字段集校验 content 字段报 Unknown field。
 */
export const queryCandidateOrders = async (
  query: IndependentBillingCandidateFilter = {}
): Promise<IndependentBillingCandidate[]> => {
  const gql = `
    query {
      sapBilling90OrderQuery {
        queryIndependentCandidateOrders(query: ${buildCandidateQueryArg(query)}) {${INDEPENDENT_CANDIDATE_CONTENT_FIELDS}        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.sapBilling90OrderQuery?.queryIndependentCandidateOrders || [];
};

/**
 * 提交创建独立开票包：后端按候选查询同一套规则重新校验所选 90 单，
 * 通过后创建开票包（编码 DLKP-yyyyMMdd-六位流水号）、生成开票行、占用 90 单并发起 SAP 打包。
 * SAP 打包失败时后端整个事务回滚，不产生脏包、不留下 90 单占用。
 */
export const createIndependentBilling = async (
  payload: IndependentBillingCreateParam
): Promise<BillingBill | null> => {
  const gql = `
    mutation {
      sapBilling90OrderMutation {
        createIndependentBill(param: ${buildCreateParamArg(payload)}) {
          id code name status billCategory totalAmount
        }
      }
    }
  `;
  const res = (await http.mutate(MODULE_NAME, gql)) as any;
  return res?.data?.sapBilling90OrderMutation?.createIndependentBill || null;
};
